<style lang="scss">
@import "../styles/payment-v2/payment-menu-content-form.scss";
</style>

<template>
  <form
    v-if="isVisible == true && paymentMethodSelected === 'add_card'"
    action="/charge"
    method="post"
    id="payment-form"
    @submit.prevent="onClickSubmitPayment"
  >
    <v-layout row wrap>
      <v-flex xs12 md6 pr-3>
        <PaymentContentSummary
          :subscription-ids="subscriptionIds"
          :default-payment-data="defaultPaymentData"
          :one-off-codes="oneOffCodes"
          :request-payment-items="requestPaymentItems"
          :add-shareholder-id="addShareholderId"
          :promo-code-enabled="promoCodeEnabled"
          :upgrade-corp-sec-subscription="upgradeCorpSecSubscription"
          :company-id-from-billing-profile="companyIdFromBillingProfile"
          :unpaidBillsDetails="unpaidBillsDetails"
        />
      </v-flex>
      <v-flex xs12 md6 class="payment-menu-content-form-left-panel-v2 new-payment-ui-method">
        <div>
          <CardSelectField
            :selected-card="selectedCard"
            :payment-methods-topic="paymentMethodsTopic"
            :credit-cards="creditCards"
          />
          <img src="/imgs/powered-by-stripe.png" class="powered-by-stripe-image" />
        </div>
        <div class="new-payment-ui-method-actions">
          <v-layout>
            <v-btn
              color="primary"
              style="width: 100%"
              :loading="isButtonLoading"
              :disabled="isButtonDisabled"
              type="submit"
              ><span class="text-uppercase font-weight-bold"
                >{{ this.$t("$onboarding.card.buttons.pay") }} {{ amountToPay }}</span
              ></v-btn
            >
          </v-layout>
        </div>
      </v-flex>
      <v-flex xs12>
        <div v-if="isPartnerPayment" style="text-align: right; margin-top: 10px">
          <v-spacer />
          <v-btn color="primary" type="button" @click="onBackClick(onBoardingTopic)"
            >&lt; {{ this.$t("$onboarding.card.buttons.back") }}</v-btn
          >
          <v-btn color="primary" :loading="isButtonLoading" :disabled="isButtonDisabled" type="submit"
            >{{ this.$t("$onboarding.card.buttons.confirm") }} ></v-btn
          >
        </div>
      </v-flex>
    </v-layout>
  </form>
</template>

<script>
/* eslint-disable */

// Proxies
import PaymentForDraftCompanyProxy from "@/proxies/back-end/credit-card-payment/create-company";
import SubscriptionPaymentPostProxy from "@/proxies/back-end/subscriptions/payment";
import AllTypePaymentPostProxy from "@/proxies/back-end/subscriptions/payment-all-type";
import PaymentTokensProxy from "@/proxies/back-end/payment-tokens/create";
import UpdateSubscriptionProxy from "@/proxies/back-end/subscriptions/update";
import UpdatePaymentRequestByTokenProxy from "@/proxies/back-end/payment-request/update";
import addNewShareholder from "@/proxies/back-end/workflow/add-new-shareholder/add-new-shareholder";
import GetCompanyProxy from "@/proxies/back-end/company/get";

// Components
import PaymentContentSummary from "./PaymentContentSummary.vue";
import CardSelectField from "./PaymentMenuContentFormCardSelect.vue";
import PaymentMethodSelect from "./PaymentMenuContentPaymentMethod.vue";

// Constants
import { Constants } from "customer-sdk/constants";

const { WORKFLOW_CONSTANTS } = Constants.WORK_FLOW;
const PAGE_URLS = Constants.PAGE_URLS;
const ON_BOARDING_EVENTS = Constants.ON_BOARDING_EVENTS;
const PAYMENT_METHODS_EVENTS = Constants.ON_BOARDING_PAYMENT_METHODS_EVENTS;
const { COMPANY_USER_MODULE, COMPANY_USERS } = Constants.COMPANY_USERS_CONSTANTS;
const CARD_SETUP_CONSTANTS = Constants.CARD_SETUP;

// Mixins
import { GoogleAnalyticsMixin } from "@/mixins/googleAnalytics";
import LocalStoreManager from "@/utilities/local-store-manager";
import { handleTask } from "@/utilities/workflow-task-handler";
import ShoppingCartManager from "@/utilities/shopping-cart-manager";
import { getAppFeatureProp } from "@/utilities/app-feature-util";
import { logEvent } from "@/utilities/analytics";

import { get, pickBy, isEmpty } from "lodash";

//#region private variables
let POST_PAYMENT_PATH = PAGE_URLS.INCORPORATION;
let store = new LocalStoreManager();
//#endregion

//#region private methods
let handlePayment = async component => {
  try {
    if (component.subscriptionIds && component.oneOffCodes && !component.requestPaymentId) {
      /*
       * Payment entry points
       * ['Onboarding Accounting Setup']
       */

      let paymentData = await handleSubscriptionAndOneOffPayment(component);
      component.parseGAData(paymentData);
      if (component.task) {
        try {
          await handleTask(component.task, paymentData, WORKFLOW_CONSTANTS.PAYMENT_STATUS.PAID);
        } catch (error) {
          console.log("error", error);
        }
      } else {
        if (component.accountingSetupOptions) {
          const {
            redirect,
            openedPage,
            enableMultiCurrency,
            questionnaire,
            questionnaire_token,
            email
          } = component.accountingSetupOptions;
          await component.updateQuestionnaireMultiCurrency(openedPage);
          const urlParams = new URLSearchParams(pickBy({ redirect, openedPage, questionnaire_token, email }));
          component.$router.push(
            `${PAGE_URLS.POST_RENEWAL_PAYMENT}?paidWith=${
              CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE
            }&${urlParams.toString()}`
          );
        } else {
          component.$router.push(
            `${PAGE_URLS.POST_RENEWAL_PAYMENT}?paidWith=${CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE}`
          );
        }
      }
    } else if (component.upgradeCorpSecSubscription) {
      /*
       * Payment entry points
       * ['Request Amend Company Share Structure']
       */
      const companyUsers = component.$store.getters[`${COMPANY_USER_MODULE}/${COMPANY_USERS}`];

      try {
        let paymentData = await handleUpgradeCorpSecSubscription(component);

        await handleTask(component.task, paymentData, WORKFLOW_CONSTANTS.PAYMENT_STATUS.PAID, companyUsers);
      } catch (error) {
        console.log("error", error);
      }
    } else if (!isEmpty(component.unpaidBillsDetails)) {
      /*
       * Payment entry points
       * ['Unpaid Bill']
       */
      try {
        let paymentData = await handleUnpaidBillPayment(component);
        component.$router.push(`${PAGE_URLS.POST_RENEWAL_PAYMENT}?paidWith=${CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE}`);
      } catch (error) {
        console.log("error", error);
      }
    } else if (component.addShareholderId) {
      let paymentData = await handleShareholderPayment(component);
      component.parseGAData(paymentData);
      component.$router.push(
        `${PAGE_URLS.POST_REQUEST_SHAREHOLDER}?paidWith=${CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE}`
      );
    } else if (component.requestPaymentId) {
      /*
       * Payment entry points
       * ['Payment Request']
       */
      let paymentData = await handleOneOffPaymentFromPaymentRequest(component);
      component.parseGAData(paymentData);
      let proxy = new UpdatePaymentRequestByTokenProxy();

      const request_payment_status = getAppFeatureProp(
        component.platformConfig.cmsGeneralFeatureList,
        "request_payment_status"
      );

      await proxy.updateRecord(component.defaultPaymentData, {
        paymentRequestId: component.requestPaymentId,
        status: request_payment_status.value.USED
      });
      component.$router.push(`${PAGE_URLS.POST_PAYMENT_REQUEST}?paidWith=${CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE}`);
    } else if (typeof component.subscriptionIds != "undefined" && !component.oneOffCodes) {
      /*
       * Payment entry points
       * ['Manual Renewal']
       */

      let paymentData = await handleSubscriptionPayment(component);
      component.parseGAData(paymentData);
      if (component.task) {
        try {
          await handleTask(component.task, paymentData, WORKFLOW_CONSTANTS.PAYMENT_STATUS.PAID);
        } catch (error) {
          console.log("error", error);
        }
      } else {
        if (component.isPartnerBillingProfilePayment) {
          component.$emit("displayAfterPaymentScreen", { paymentMethod: "creditCard" });
        } else {
          component.$router.push(
            `${PAGE_URLS.POST_RENEWAL_PAYMENT}?paidWith=${CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE}`
          );
        }
      }
    } else if (component.isPartnerPayment) {
      let paymentData = await handleOnboardingPayment(component);
      component.parseGAData(paymentData);
      publishPaymentSubmitted(component);
    } else {
      /*
       * Payment entry points
       * ['Onboarding']
       */
      let paymentData = await handleOnboardingPayment(component);
      component.parseGAData(paymentData);
      const new_payment_page_enabled = getAppFeatureProp(
        component.platformConfig.cmsAppFeatures,
        "new_payment_page_enabled"
      );

      let isNewPaymentPage = new_payment_page_enabled.enabled || false;
      setTimeout(() => {
        if (isNewPaymentPage) {
          component.$store.dispatch("companyUsersModule/updateCompanyDeleteIcon");
          component.$store.dispatch("companyUsersModule/hideShoppingCartAfterPayment");
          component.$store.dispatch("companyUsersModule/hideShoppingCartDropdown");
          component.$emit("displayAfterPaymentScreen", { paymentMethod: "creditCard" });
        } else {
          component.$router.push(`${POST_PAYMENT_PATH}?isInitial=true`);
        }
      }, 5000);
    }
  } catch (error) {
    console.error(error);
    publishSubmitPaymentError(component, error.message);
    throw error;
  }
};

let handleOnboardingPayment = async component => {
  try {
    let proxy = new PaymentForDraftCompanyProxy();
    let payload = {
      card_id: component.selectedCard,
      payment_token: component.paymentToken,
      is_partner_payment: component.isPartnerPayment
    };
    return await proxy.execute(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let handleSubscriptionPayment = async component => {
  try {
    let proxy = new SubscriptionPaymentPostProxy();
    let payload = {
      card_id: component.selectedCard,
      companySubscriptionIds: component.subscriptionIds,
      payment_token: component.paymentToken,
      invoice_tag: component.entryPoint
    };
    return await proxy.execute(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

const handleUpgradeCorpSecSubscription = async component => {
  const proxy = new AllTypePaymentPostProxy();
  const company_id = component.defaultPaymentData.id;
  const amendCompanyShareStructureData = localStorage.getItem(`amendCompanyShareStructureData_${company_id}`);
  const shareholders = get(JSON.parse(amendCompanyShareStructureData), "shareholders", []);
  const totalNumberOfShareholder = shareholders.filter(shareholder => !shareholder.is_removed).length;

  try {
    let payload = {
      company_id,
      card_id: component.selectedCard,
      payment_token: component.paymentToken,
      total_number_of_shareholder: totalNumberOfShareholder,
      invoice_tag: component.entryPoint
    };

    return await proxy.upgradeCorpSecSubscriptionPayment(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

const handleUnpaidBillPayment = async component => {
  const proxy = new AllTypePaymentPostProxy();
  const company_id = component.defaultPaymentData.id;
  try {
    let payload = {
      company_id,
      card_id: component.selectedCard,
      payment_token: component.paymentToken,
      unpaidBillsDetails: component.unpaidBillsDetails,
      invoice_tag: component.entryPoint
    };

    return await proxy.unpaidBillPayment(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let handleSubscriptionAndOneOffPayment = async component => {
  try {
    let proxy = new AllTypePaymentPostProxy();
    let payload = {
      card_id: component.selectedCard,
      companySubscriptionIds: component.subscriptionIds,
      payment_token: component.paymentToken,
      oneOffCodes: component.oneOffCodes,
      companyId:
        localStorage.getItem("paymentRequestCompanyId") && localStorage.getItem("paymentRequestCompanyId") != "null"
          ? localStorage.getItem("paymentRequestCompanyId")
          : localStorage.getItem("companyId")
    };
    return await proxy.postPayment(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let buildPayload = async component => {
  const cart = new ShoppingCartManager();
  let response = await cart.fetchUpgradeCorpsecSubscriptionDetails(component.addShareholderId);
  let companyProxy = new GetCompanyProxy();
  let company = await companyProxy.execute({ id: await store.getCompanyId(), authToken: store.getToken() });

  const companyId =
    localStorage.getItem("paymentRequestCompanyId") && localStorage.getItem("paymentRequestCompanyId") != "null"
      ? localStorage.getItem("paymentRequestCompanyId")
      : localStorage.getItem("companyId");
  const payload = {
    authToken: store.getToken(),
    payload: {
      requested_by_user_id: store.getUserId(),
      requested_by_user_name: store.getUserEmail(),
      company_user_id: store.getUserId(),
      company_user_name: store.getUserEmail(),
      company_id: companyId,
      company_name: company ? company.name : "",
      price: response ? response.shoppingCartTotal : 0,
      company_workflow_data: {
        request_information: {
          request_type: "addition_of_shareholder",
          company_id: companyId,
          company_name: company ? company.name : "",
          requested_by_user_id: store.getUserId()
        }
      }
    }
  };
  return payload;
};

let handleShareholderPayment = async component => {
  try {
    let proxy = new AllTypePaymentPostProxy();
    let payload = {
      card_id: component.selectedCard,
      payment_token: component.paymentToken,
      addShareholderId: component.addShareholderId,
      companyId: localStorage.getItem("paymentRequestCompanyId")
        ? localStorage.getItem("paymentRequestCompanyId")
        : localStorage.getItem("companyId")
    };
    // start workflow
    let workflowPayload = await buildPayload(component);
    await addNewShareholder.startAdditionOfShareholder(workflowPayload);

    return await proxy.postUpgradePayment(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let handleOneOffPaymentFromPaymentRequest = async component => {
  try {
    let proxy = new AllTypePaymentPostProxy();
    let payload = {
      card_id: component.selectedCard,
      payment_token: component.paymentToken,
      companySubscriptionIds: component.subscriptionIds,
      oneOffCodes: component.oneOffCodes,
      companyId: localStorage.getItem("paymentRequestCompanyId")
        ? localStorage.getItem("paymentRequestCompanyId")
        : localStorage.getItem("companyId"),
      requestPaymentId: component.requestPaymentId,
      requestPaymentItems: component.requestPaymentItems,
      invoice_tag: component.entryPoint
    };
    return await proxy.postPayment(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let initCreatePaymentToken = async component => {
  try {
    component.paymentToken = "";

    let payload = {
      authToken: store.getToken()
    };
    let result = await PaymentTokensProxy.createPaymentToken(payload);
    component.paymentToken = result.payment_token;
  } catch (error) {
    throw error;
  }
};

let subscribeToTopic = component => {
  if (component.paymentMethodsTopic) {
    component.paymentMethodsTopic.subscribe(msg => {
      if (msg.type === PAYMENT_METHODS_EVENTS.ON_EXISTING_CARD_SELECTED) {
        if (msg.data.selectedCard) component.selectedCard = msg.data.selectedCard;
        component.isVisible = true;
      } else if (msg.type === PAYMENT_METHODS_EVENTS.ON_ADD_NEW_CARD_SELECTED) {
        component.isVisible = false;
      } else if (msg.type === PAYMENT_METHODS_EVENTS.ON_BANK_TRANSFER_CLICKED) {
        component.isVisible = false;
      }
    });
  }
};

let publishSubmitPaymentError = (component, message) => {
  let { paymentMethodsTopic } = component;
  if (paymentMethodsTopic) {
    paymentMethodsTopic.publish({
      type: PAYMENT_METHODS_EVENTS.ON_SUBMIT_PAYMENT_ERROR,
      data: message
    });
  }
};

let checkIfComponentShouldLoad = component => {
  let cards = component.creditCards;
  let userDontHaveCard = cards === null || !(cards instanceof Array) || cards.length === 0;

  if (userDontHaveCard) {
    return false;
  } else {
    let defaultCardOnly = cards.length === 1 && cards[0].value === CARD_SETUP_CONSTANTS.ADD_CARD_KEY;

    return !defaultCardOnly;
  }
};

let getDefaultPaymentCard = component => {
  if (isEmpty(component.creditCards)) return;
  let creditCard = component.creditCards.find(card => card.is_primary === true);
  if (creditCard) {
    component.selectedCard = creditCard.value;
    return;
  }
  component.selectedCard = component.creditCards[0].value;
};

let getComponentData = component => {
  return {
    lastDoneStep: 4
  };
};

let publishPaymentSubmitted = component => {
  if (component.onBoardingTopic) {
    component.onBoardingTopic.publish({
      type: ON_BOARDING_EVENTS.ON_PAYMENT_SUBMITTED,
      data: getComponentData(component)
    });
  }
};
//#endregion

export default {
  components: {
    PaymentContentSummary,
    CardSelectField,
    PaymentMethodSelect
  },
  mixins: [GoogleAnalyticsMixin],
  props: {
    onBoardingTopic: Object,
    paymentMethodsTopic: Object,
    defaultPaymentData: Object,
    creditCards: Array,
    subscriptionIds: Array,
    unpaidBillsDetails: Array,
    task: Object,
    oneOffCodes: Array,
    requestPaymentId: String,
    requestPaymentItems: Array,
    platformConfig: Object,
    isPartnerPayment: Boolean,
    addShareholderId: String,
    promoCodeEnabled: Boolean,
    accountingSetupOptions: Object,
    updateQuestionnaireMultiCurrency: Function,
    upgradeCorpSecSubscription: { type: Boolean, default: false },
    isPartnerBillingProfilePayment: {
      type: Boolean,
      default: false
    },
    shoppingCartTotal: Number,
    paymentMethodSelected: String,
    companyIdFromBillingProfile: String,
    entryPoint: String
  },
  data() {
    return {
      isVisible: false,
      isButtonLoading: false,
      selectedCard: null,
      paymentToken: "",
      currency: {}
    };
  },
  computed: {
    isButtonDisabled() {
      let component = this;

      if (component.paymentToken === "") {
        return true;
      }

      return false;
    },
    hasBackButtonListener() {
      return this.$parent.$listeners && this.$parent.$listeners.triggerBackButton instanceof Function;
    },
    amountToPay() {
      let total = 0;

      if (!this.requestPaymentItems || this.requestPaymentItems.length === 0)
        return this.currency.symbol + " " + Number(this.shoppingCartTotal).toLocaleString();

      this.requestPaymentItems.map(item => {
        total += item.service_unit_price * item.quantity;
      });

      return this.currency.symbol + " " + Number(total).toLocaleString();
    }
  },
  methods: {
    onBackClick: function(onBoardingTopic) {
      if (onBoardingTopic) {
        onBoardingTopic.publish({
          type: ON_BOARDING_EVENTS.ON_CONTACT_SELECTED
        });
      }
    },
    onBackEvent: function() {
      // this.$parent.$emit("triggerBackButton");
    },
    logMixpanelEvents: async function (component) {
      const mixpanelTaskTypesToTrack = [
        {
          eventName: "Complete payment for ND appointment request",
          taskType: 'appointmentofdirector',
        },
        {
          eventName: "Complete payment for ND change request",
          taskType: "changeofdirector",
        },
      ];

      const findTaskType = mixpanelTaskTypesToTrack.find(taskType => taskType.taskType === component.task.type);

      if (findTaskType) {
        await logEvent.handleWriteToMixpanel(findTaskType.eventName, {
          "User Id": localStorage.getItem("userId"),
          "Company ID": localStorage.getItem("companyId"),
          "Payment Type": component.paymentMethodSelected
        });
      }

    },
    onClickSubmitPayment: async function() {
      let component = this;


      try {
        component.isButtonLoading = true;
        await handlePayment(component);

      } catch (error) {
        component.isButtonLoading = false;
        throw error;
      } finally {
        await this.logMixpanelEvents(component)
      }
    }
  },
  created() {
    let component = this;
    component.isVisible = checkIfComponentShouldLoad(component);
    initCreatePaymentToken(component);
    getDefaultPaymentCard(component);
    subscribeToTopic(component);
    component.currency = component.platformConfig.currency;
  }
};
</script>